import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  Container,
  Grid,
  InputAdornment,
  IconButton,
  InputLabel,
  Button,
  CircularProgress,
} from '@mui/material';
import Controls from '../components/FormsUI';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../utility/auth';
import LoginController from '../controller/LoginController';
import LoginCaptchaController from '../controller/LoginCaptchaController';
import CustomizedSnackbars from '../components/Alert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Logo from '../assets/images/logo.png';
import '../css/style.css';
import ExceptionHandlerController from '../controller/ErrorLogsController';
import CachedIcon from '@mui/icons-material/Cached';

const INITIAL_FORM_STATE = { username: '', password: '' };
const FORM_VALIDATION = Yup.object().shape({
  username: Yup.string()
    .email()
    .trim('No white space allowed')
    .strict(true)
    .required('Email Id is required'),
  password: Yup.string()
    .trim('No white space allowed')
    .min(8)
    .strict(true)
    .required('Password is required'),
});

const apiMessages = {
  NO_PAYMENT_DONE: 'Please register again and submit your payment details.',
  SUSPENDED:
    'Your account has been temporarily disconnected. Kindly contact the administrator or reset your password.',
  ACCOUNT_LOCKED: 'Your account has been locked due to multiple failed login attempts.',
};

const loginController = new LoginController();
const loginCaptchaController = new LoginCaptchaController();
const exceptionHandlerController = new ExceptionHandlerController();

const getDate = () => {
  let caughtError;
  try {
    return (
      '' +
      (new Date()?.getDate()?.toString()?.length === 1
        ? '0' + new Date()?.getDate()
        : new Date()?.getDate()) +
      '/' +
      (Number(new Date()?.getMonth() + 1)?.toString()?.length === 1
        ? '0' + Number(new Date()?.getMonth() + 1)
        : Number(new Date()?.getMonth() + 1)) +
      '/' +
      new Date()?.getFullYear() +
      '  ' +
      new Date()?.toLocaleTimeString()
    );
  } catch (error) {
    caughtError = error;
    let logData = {};
    logData.message = caughtError?.message;
    logData.stack = caughtError?.stack;
    let logError = { logData, module: 'Login getdate method' };
    exceptionHandlerController.HandleException(logError);
  }
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();

  const [alert, setAlert] = useState({ counter: 0, type: '', msg: '' });
  const [IP, setIP] = useState('');
  const [togglePassword, setTogglePassword] = useState(true);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [loading, setLoading] = useState(false);

  // Account Lockout State
  const [accountLocked, setAccountLocked] = useState(false);
  const [remainingMinutes, setRemainingMinutes] = useState(0);
  const [lockoutExpiry, setLockoutExpiry] = useState(null);

  // Captcha State
  const [captcha, setCaptcha] = useState({ id: '', image: '' });
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);

  const fetchCaptcha = async () => {
    setCaptchaLoading(true);
    try {
      const res = await loginCaptchaController.Login();
      const data = res.data;
      setCaptcha({ id: data.captchaId, image: data.image });
      setCaptchaInput('');
    } catch (e) {
      console.error('Failed to load captcha', e);
    } finally {
      setCaptchaLoading(false);
    }
  };

  useEffect(() => {
    fetchCaptcha();
  }, []);

  useEffect(() => {
    async function ipAddress() {
      await fetch('https://api.ipify.org/?format=json')
        .then((response) => response?.json())
        .then((data) => setIP(() => data?.ip))
        .catch((error) => console.error('error', error));
    }
    ipAddress();
  }, []);

  // Countdown timer for account lockout
  useEffect(() => {
    if (!accountLocked || !lockoutExpiry) return;

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const timeLeft = lockoutExpiry - now;

      if (timeLeft <= 0) {
        setAccountLocked(false);
        setRemainingMinutes(0);
        setLockoutExpiry(null);
        showAlert('info', 'Account lockout expired. You can now try logging in again.');
        clearInterval(timer);
      } else {
        const minutes = Math.ceil(timeLeft / (1000 * 60));
        setRemainingMinutes(minutes);
      }
    }, 1000); // Update every second for smooth countdown

    return () => clearInterval(timer);
  }, [accountLocked, lockoutExpiry]);

  const showAlert = (type, msg) => {
    setAlert((prev) => ({ counter: prev.counter + 1, type, msg }));
  };

  const handleLogin = (data) => {
    try {
      if (captchaInput === '') return showAlert('error', 'Please enter the captcha');
      data.IP = IP;
      data.LastLoginDateTime = getDate();
      data.captchaId = captcha.id;
      data.captchaInput = captchaInput;
      loginController
        .Login(data)
        .then((response) => {
          handleLoginResponse(response);
        })
        .catch(() => {
          showAlert('error', 'Oops something went wrong.');
        });
    } catch (error) {
      handleLoginError(error);
    }
  };

  const handleLoginResponse = (response) => {
    // Handle rate limiting (429 status)
    if (response?.status === 429) {
      return showAlert(
        'error',
        response?.data?.message ||
        'Too many login attempts. Please wait 15 minutes before trying again.',
      );
    }

    // Handle account locked (423 status) - Account lockout with password reset or timeout options
    if (response?.status === 423) {
      const lockoutData = response?.data?.body;
      const remainingMin = lockoutData?.remainingMinutes || 15;

      setAccountLocked(true);
      setRemainingMinutes(remainingMin);
      // Set expiry time for auto-unlock countdown
      const expiryTime = new Date().getTime() + remainingMin * 60 * 1000;
      setLockoutExpiry(expiryTime);
      // Show informative alert
      showAlert(
        'error',
        `Account locked due to multiple failed login attempts. You have ${remainingMin} minute${remainingMin !== 1 ? 's' : ''} remaining.`,
      );
      // Auto-switch to forgot password mode after 2 seconds
      setTimeout(() => {
        setForgotPasswordMode(true);
        setForgotEmail('');
      }, 2000);
      return;
    }

    if (response?.status !== 200) {
      const remainingAttempts = response?.data?.body?.remainingAttempts;

      let errorMsg = response?.data?.message || response?.response?.data?.message;
      // for invalid captcha, we will show a specific message and refresh the captcha
      if (errorMsg === 'Invalid captcha') {
        showAlert('error', 'Enter captcha was wrong, please try again');
        fetchCaptcha();
        return;
      }
      if (remainingAttempts !== undefined) {
        errorMsg =
          errorMsg ||
          `Invalid credentials - ${remainingAttempts} attempt${remainingAttempts !== 1 ? 's' : ''} remaining`;
      }
      showAlert('error', errorMsg);
      fetchCaptcha();
      return;
    }

    const message = response?.data?.message;
    if (message === apiMessages?.NO_PAYMENT_DONE || message === apiMessages?.SUSPENDED) {
      showAlert('info', message);
      return;
    }
    let decryptedData = response?.data?.body;
    if (decryptedData?.token) {
      // Clear lockout state on successful login
      setAccountLocked(false);
      setRemainingMinutes(0);
      setLockoutExpiry(null);

      auth?.login(decryptedData?.token);
      const user = auth?.decoder(decryptedData?.token);
      if (user) {
        processUserLogin(user, decryptedData);
      }
    }
  };

  const processUserLogin = (user, decryptedData) => {
    sessionStorage.setItem('LastLogin', decryptedData?.lastLogin);
    sessionStorage.setItem('TenantName', decryptedData?.tenantName);

    showAlert('success', 'Login Successfully');
    setTimeout(() => {
      navigateBasedOnRole(user?.data?.UserInfo?.access);
    }, 100);
  };

  const navigateBasedOnRole = (menuAccess) => {
    if (!menuAccess || !Array.isArray(menuAccess)) return;
    const dashboardAccess = menuAccess.find((item) => item.menuName === 'Dashboard');
    if (dashboardAccess && dashboardAccess.path) {
      window.location.href = dashboardAccess.path.startsWith('/')
        ? dashboardAccess.path
        : `/${dashboardAccess.path}`;
      return;
    }

    for (const menu of menuAccess) {
      if (menu.path) {
        window.location.href = menu.path.startsWith('/') ? menu.path : `/${menu.path}`;
        return;
      }

      if (Array.isArray(menu.submenus)) {
        for (const submenu of menu.submenus) {
          if (submenu.path) {
            window.location.href = submenu.path.startsWith('/') ? submenu.path : `/${submenu.path}`;
            return;
          }
        }
      }
    }

    navigate('/login');
  };

  const handleLoginError = (error) => {
    let logData = {
      message: error?.message,
      stack: error?.stack,
    };
    let logError = { logData, module: 'Login Login method' };
    exceptionHandlerController.HandleException(logError);
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail || !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(forgotEmail)) {
      showAlert('error', 'Please enter a valid email address.');
      return;
    }
    setLoading(true);

    loginController
      .ForgotPassword({ email: forgotEmail })
      .then((response) => {
        if (response?.status === 200) {
          showAlert('success', 'A new password has been sent to your email.');

          // Clear account lockout on successful password reset
          setAccountLocked(false);
          setRemainingMinutes(0);
          setLockoutExpiry(null);

          setForgotEmail('');
          setForgotPasswordMode(false);
        } else {
          showAlert('error', response?.message || 'Email not found.');
        }
      })
      .catch(() => {
        showAlert('error', 'Oops something went wrong.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const forgotToggleButtonStyles = {
    border: '2px solid white',
    padding: '10px 20px',
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': {
      borderColor: '#5b4beeff',
      backgroundColor: 'rgba(236, 230, 230, 1)',
      color: '#000',
    },
  };

  return (
    <Grid container>
      <Grid item xs={12} className="loginlogo">
        <img src={Logo} alt="Logo" />
      </Grid>

      <Grid item xs={12} className="login-bg">
        <Container maxWidth="md">
          <div>
            <Formik
              initialValues={{ ...INITIAL_FORM_STATE }}
              validationSchema={FORM_VALIDATION}
              onSubmit={(values) => handleLogin(values)}
            >
              {({ handleSubmit }) => (
                <Form>
                  <Grid container spacing={2} className="page-container">
                    <Grid item xs={12}>
                      <Grid className="heading">
                        <h3>{forgotPasswordMode ? 'Forgot Password' : 'Login'}</h3>
                      </Grid>
                    </Grid>

                    {/* Account Lockout Status Display */}
                    {accountLocked && (
                      <Grid
                        item
                        xs={12}
                        sx={{
                          backgroundColor: '#fff3cd',
                          border: '2px solid #ffc107',
                          borderRadius: '8px',
                          padding: '20px',
                          textAlign: 'center',
                        }}
                      >
                        <div style={{ marginBottom: '15px' }}>
                          <h4
                            style={{
                              color: '#856404',
                              margin: '0 0 10px 0',
                              fontSize: '18px',
                              fontWeight: 600,
                            }}
                          >
                            ⚠️ Account Temporarily Locked
                          </h4>
                          <p style={{ color: '#856404', margin: '0 0 15px 0', fontSize: '14px' }}>
                            Due to multiple failed login attempts
                          </p>
                        </div>

                        <div
                          style={{
                            backgroundColor: '#fff',
                            padding: '15px',
                            borderRadius: '6px',
                            marginBottom: '15px',
                            border: '1px solid #ffc107',
                          }}
                        >
                          <div
                            style={{
                              fontSize: '24px',
                              fontWeight: 700,
                              color: '#ffc107',
                              marginBottom: '5px',
                            }}
                          >
                            {remainingMinutes}:
                            {String(
                              Math.floor(((lockoutExpiry - new Date().getTime()) % 60000) / 1000),
                            ).padStart(2, '0')}
                          </div>
                          <p style={{ fontSize: '12px', color: '#666', margin: '0' }}>
                            Time remaining before automatic unlock
                          </p>
                        </div>

                        <div
                          style={{
                            backgroundColor: '#e8f4f8',
                            padding: '15px',
                            borderRadius: '6px',
                            marginBottom: '15px',
                            border: '1px solid #b3dfe9',
                          }}
                        >
                          <p
                            style={{
                              fontSize: '13px',
                              color: '#0c4e7f',
                              fontWeight: 500,
                              margin: '0 0 10px 0',
                            }}
                          >
                            You can unlock your account by:
                          </p>
                          <div style={{ textAlign: 'left', fontSize: '12px', color: '#0c4e7f' }}>
                            <p style={{ margin: '5px 0' }}>
                              ✓ <strong>Option 1:</strong> Click "Forgot Password" below to reset
                              your password (immediate unlock)
                            </p>
                            <p style={{ margin: '5px 0' }}>
                              ✓ <strong>Option 2:</strong> Wait {remainingMinutes} minute
                              {remainingMinutes !== 1 ? 's' : ''} for automatic unlock
                            </p>
                          </div>
                        </div>

                        <p style={{ fontSize: '12px', color: '#856404', margin: '0' }}>
                          Choose either option above to regain access to your account
                        </p>
                      </Grid>
                    )}

                    {forgotPasswordMode ? (
                      <>
                        {/* Show reason when locked account goes to forgot password */}
                        {accountLocked && (
                          <Grid
                            item
                            xs={12}
                            sx={{
                              backgroundColor: '#e3f2fd',
                              border: '1px solid #2196f3',
                              borderRadius: '6px',
                              padding: '12px',
                              marginBottom: '10px',
                            }}
                          >
                            <p style={{ margin: '0', fontSize: '13px', color: '#1565c0' }}>
                              <strong>ℹ️ Password Reset Required:</strong> Your account was locked
                              due to multiple failed login attempts. Reset your password to unlock
                              your account immediately.
                            </p>
                          </Grid>
                        )}

                        <Grid item xs={12}>
                          <Controls.Textfield
                            name="forgotEmail"
                            label="Enter your Email"
                            value={forgotEmail}
                            onChange={(e) => setForgotEmail(e.target.value)}
                            autoFocus
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={handleForgotPassword}
                            disabled={loading}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                          >
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
                          </Button>
                        </Grid>

                        <Grid item xs={12}>
                          <Button
                            sx={forgotToggleButtonStyles}
                            onClick={() => {
                              setForgotPasswordMode(false);
                              setForgotEmail('');
                            }}
                          >
                            <b>{accountLocked ? 'Back to Lockout Info' : 'Login'}</b>
                          </Button>
                        </Grid>
                      </>
                    ) : (
                      <>
                        {/* Show lockout message when account is locked */}
                        {accountLocked && (
                          <Grid item xs={12} sx={{ textAlign: 'center', marginBottom: '15px' }}>
                            <p style={{ color: '#d32f2f', fontSize: '14px', fontWeight: 500 }}>
                              Login is disabled while your account is locked. Please use the options
                              above.
                            </p>
                          </Grid>
                        )}

                        <Grid item xs={12}>
                          <Controls.Textfield
                            name="username"
                            label="Email Id"
                            type="string"
                            inputProps={{ maxLength: 50 }}
                            autoFocus
                            disabled={accountLocked}
                            onKeyDown={(e) => {
                              if (e.keyCode === 13) handleSubmit(e);
                            }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Controls.Textfield
                            name="password"
                            label="Password"
                            type={togglePassword ? 'password' : 'string'}
                            inputProps={{ maxLength: 50 }}
                            disabled={accountLocked}
                            onKeyDown={(e) => {
                              if (e.keyCode === 13) handleSubmit(e);
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => setTogglePassword((prev) => !prev)}
                                    disabled={accountLocked}
                                  >
                                    {togglePassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Controls.Textfield
                              name="captchaInput"
                              label="Enter the given Captcha"
                              value={captchaInput}
                              onChange={(e) => setCaptchaInput(e.target.value)}
                              inputProps={{ maxLength: 10 }}
                            />
                            <div
                              dangerouslySetInnerHTML={{ __html: captcha.image }}
                              style={{ border: '1px solid #ccc', borderRadius: 4 }}
                            />
                            <div
                              type="button"
                              onClick={fetchCaptcha}
                              disabled={captchaLoading}
                              size="small"
                              style={{
                                cursor: captchaLoading ? 'not-allowed' : 'pointer',
                              }}
                            >
                              {captchaLoading ? <CircularProgress size={18} /> : <CachedIcon />}
                            </div>
                          </div>
                        </Grid>
                        {location?.state?.data === 'expired' ||
                          location?.state?.state?.data === 'expired' ? (
                          <InputLabel
                            className="infotext infonominee mob-ipvsvg"
                            sx={{
                              color: '#d32f2f',
                              font: 'inherit',
                              paddingLeft: '10px',
                            }}
                          >
                            Your session was expired. Please login again
                          </InputLabel>
                        ) : null}

                        <Grid item xs={12}>
                          <Controls.Button disabled={accountLocked}>
                            {accountLocked ? 'Account Locked' : 'Login'}
                          </Controls.Button>
                        </Grid>

                        <Grid item xs={12}>
                          <Button
                            sx={forgotToggleButtonStyles}
                            onClick={() => {
                              setForgotPasswordMode(true);
                              setForgotEmail('');
                            }}
                          >
                            <b>Forgot Password</b>
                          </Button>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Form>
              )}
            </Formik>

            {alert.counter > 0 && (
              <CustomizedSnackbars status={alert.counter} msg={alert.msg} type={alert.type} />
            )}
          </div>
        </Container>
      </Grid>
    </Grid>
  );
};

export default Login;
